
import { createContext, useContext, useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { useTheme } from "next-themes"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { SEOHead } from "@/components/seo-head"

const brandRed = "#C0392B"
const brandGreen = "#4A8C3F"
const brandBlue = "#2E5BA8"

const brandRedLight = "#E74C3C"
const brandGreenLight = "#6ECB63"
const brandBlueLight = "#5B9BD5"

// ─── i18n ────────────────────────────────────────────────────────────────────

type Lang = "en" | "sq" | "it"
type ModuleKey = "business" | "classes" | "workout" | "diet" | "store" | "whitelabel" | "marketing" | "payments"

const LANGS: { code: Lang; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "sq", label: "Shqip",   flag: "🇦🇱" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
]

interface T {
  nav: { signIn: string; getStarted: string }
  hero: {
    badge: string; heading1: string; heading2: string
    description: string; cta1: string; cta2: string
    footnote: string; membersLabel: string
  }
  stats: { gyms: string; activeMembers: string; uptimeSla: string; support: string }
  features: {
    heading: string; subheading: string
    cards: { title: string; desc_short: string; desc: string }[]
  }
  pricing: {
    heading: string; subheading: string
    comingSoon: string; soon: string; perMonth: string; perMonthEach: string
    modules: Record<ModuleKey, { name: string; features: string[] }>
    extraLocations: { name: string; features: string[] }
    calculator: {
      title: string; selectAll: string; extraLocations: string
      modules: (n: number) => string; extraLocs: (n: number) => string; total: string
    }
    cta: { sub: string; btn1: string; btn2: string }
  }
  showcase: {
    heading: string; subheading: string
    tabs: { diet: string; workout: string; promo: string }
    comingSoon: string
    dietDesc: string; workoutDesc: string; promoDesc: string
  }
  benefits: { heading: string; subheading: string; items: { title: string; desc: string }[] }
  caseStudy: {
    label: string; heading1: string; heading2: string; description: string
    gyms: { subtitle: string; members: string; highlight: string; features: string[] }[]
    followInstagram: string; testimonial: string; operator: string; statsLabels: string[]
  }
  cta: {
    heading1: string; heading2: string; description: string
    btn1: string; btn2: string; check1: string; check2: string; check3: string
  }
  footer: {
    tagline: string
    legal: string; legalLinks: string[]
    copyright: string
  }
}

const translations: Record<Lang, T> = {
  // ── English ────────────────────────────────────────────────────────────────
  en: {
    nav: { signIn: "Sign In", getStarted: "Get Started" },
    hero: {
      badge: "AI-Powered Fitness Platform",
      heading1: "Transform Your",
      heading2: "Fitness Business with AI",
      description: "The AI-powered platform for gym and fitness center management. Automate operations, personalise member experiences, and grow your business — all driven by intelligent automation.",
      cta1: "Start Free Trial",
      cta2: "Schedule Demo",
      footnote: "No credit card required · 14-day free trial · Cancel anytime",
      membersLabel: "Members",
    },
    stats: { gyms: "Gyms", activeMembers: "Active Members", uptimeSla: "Uptime SLA", support: "Support" },
    features: {
      heading: "AI-Driven Tools to Scale",
      subheading: "Intelligent modules that automate, optimise, and personalise every aspect of your fitness business",
      cards: [
        { title: "White-Label Multi-Tenancy",    desc_short: "Built for franchises and chains",      desc: "Manage unlimited locations from one platform. AI-powered insights surface performance trends per site, while each gym keeps custom branding and full independence." },
        { title: "AI Promo Builder",                 desc_short: "Boost revenue with AI promos",        desc: "AI designs targeted promotions, finds the best offer timing, and personalises discounts to maximise conversions — turning member data into revenue growth." },
        { title: "AI-Smart Class Scheduling",      desc_short: "AI-optimised booking management",     desc: "AI analyses attendance patterns and demand to suggest optimal schedules, auto-fill waitlists, and balance trainer workloads." },
        { title: "Payment Processing",            desc_short: "Automated revenue management",        desc: "Recurring billing, payment plans, invoicing, and AI-generated financial forecasts. Accept all major payment methods." },
        { title: "AI-Powered Analytics",           desc_short: "Predictive business intelligence",    desc: "AI surfaces revenue trends, predicts peak hours, identifies at-risk members, and delivers actionable recommendations in real time." },
        { title: "AI Nutrition & Training Plans",  desc_short: "AI-generated member programs",        desc: "AI builds personalised diet and workout plans based on each member's goals, body metrics, and progress — and adapts them automatically." },
      ],
    },
    pricing: {
      heading: "Flexible Modular Pricing",
      subheading: "Pay only for the modules you need. Add or remove features as your business grows.",
      comingSoon: "COMING SOON", soon: "SOON", perMonth: "/month", perMonthEach: "/month each",
      modules: {
        business:   { name: "Business Management", features: ["Member Check-Ins (QR, Card, PIN)", "Multi-Location Dashboard", "Employee Management", "Reports & Analytics"] },
        classes:    { name: "Classes",             features: ["Scheduling (recurring & one-off)", "Self-service bookings & waitlists", "Capacity & trainer assignment", "Automated notifications"] },
        workout:    { name: "Workout & Training",  features: ["Workout Plan Creator", "Workout Logger", "Exercise Library", "Performance Reporting"] },
        diet:       { name: "Diet Planner",        features: ["Personalized meal plans", "Food database (local foods)", "Calorie & macro tracking", "Workout plan integration"] },
        store:      { name: "Store",               features: ["Product catalog & categories", "Order management & tracking", "Configurable order workflows", "Food, supplements, merch & more"] },
        whitelabel: { name: "White Label",         features: ["Personal iOS & Android apps", "Personalized website", "Custom domain", "Full brand customization"] },
        marketing:  { name: "Marketing & CRM",     features: ["CRM with member profiles", "Automated renewal reminders", "Email, SMS, Push & WhatsApp", "Onboarding automation"] },
        payments:   { name: "Payments",            features: ["Stripe & PayPal Integration", "Pokpay (local fintech)", "Recurring billing", "Payment reports & tracking"] },
      },
      extraLocations: { name: "Extra Locations", features: ["Full module access per location", "Separate management & reports", "Custom branding per gym", "Independent operations"] },
      calculator: {
        title: "Pricing Calculator", selectAll: "Select All", extraLocations: "Extra Locations",
        modules: (n) => `${n} module${n !== 1 ? "s" : ""}`,
        extraLocs: (n) => `+${n} extra location${n !== 1 ? "s" : ""}`,
        total: "Total",
      },
      cta: { sub: "Start with the essentials and add modules as you grow", btn1: "Get Started Now", btn2: "Talk to Sales" },
    },
    showcase: {
      heading: "See AI in Action",
      subheading: "Each gym gets its own fully branded experience. Here's how AI tools look across different fitness businesses on our platform.",
      tabs: { diet: "AI Diet Planner", workout: "AI Workout Builder", promo: "AI Promo Builder" },
      comingSoon: "COMING SOON",
      dietDesc: "AI generates personalised meal plans with macro tracking, food databases, and automatic adjustments — all branded to your gym.",
      workoutDesc: "Smart training programs built around fitness level, recovery data, and performance trends — plans evolve as members progress.",
      promoDesc: "AI-powered promotions that target the right members at the right time to maximise revenue and retention.",
    },
    benefits: {
      heading: "Why THE NOT FIT?",
      subheading: "The most advanced AI-powered fitness management platform, trusted by industry leaders",
      items: [
        { title: "AI-Powered Diet Management",       desc: "Generate personalised nutrition plans in seconds. AI adapts macros and meal schedules based on each member's goals, allergies, and body metrics." },
        { title: "AI Workout Plan Builder",           desc: "Smart training programs built around fitness level, recovery data, and performance trends. Plans evolve automatically as members progress." },
        { title: "AI-Verified Access Control",        desc: "AI-driven identity checks via QR code, NFC card, PIN, or biometric scan. Anomaly detection flags suspicious entries across all locations in real time." },
        { title: "AI Progress Insights",              desc: "AI analyses body composition, strength records, and cardio benchmarks to surface trends and predict plateaus — giving trainers and members clear next steps." },
        { title: "AI Retention Engine",               desc: "Machine-learning models predict churn before it happens. AI triggers personalised re-engagement offers, streak rewards, and win-back campaigns automatically." },
        { title: "AI Operations Dashboard",           desc: "AI forecasts floor occupancy, predicts class demand, optimises trainer schedules, and highlights revenue opportunities — live across every location." },
      ],
    },
    caseStudy: {
      label: "Case Study", heading1: "Powering the", heading2: "Flex Gym Group",
      description: "10,000+ members across multiple locations — managed seamlessly on THE NOT FIT platform. From premium gyms to CrossFit studios to in-house restaurants, one system does it all.",
      gyms: [
        { subtitle: "Premium Gym in the Capital", members: "2,000+", highlight: "Members", features: ["Full equipment floor", "Group classes", "Personal training", "Sauna & recovery"] },
        { subtitle: "Premium Seaside Gym",         members: "1,500+", highlight: "Members", features: ["Seaside training", "Outdoor areas", "Seasonal programs", "Resort integration"] },
        { subtitle: "CrossFit, Pilates & Restaurant", members: "1,200+", highlight: "Members", features: ["CrossFit box", "Pilates studio", "In-house restaurant", "Sports nutrition bar"] },
      ],
      followInstagram: "Follow on Instagram",
      testimonial: '"THE NOT FIT lets us manage 3 completely different gym concepts — from a premium fitness center to a CrossFit box with a restaurant — all under one platform. Our members love the seamless experience."',
      operator: "Multi-location fitness operator, Albania",
      statsLabels: ["Locations", "Total Members", "Classes / Week", "Platform"],
    },
    cta: {
      heading1: "Ready to Scale Your", heading2: "Fitness Empire with AI?",
      description: "Join the growing number of fitness businesses using AI-powered tools from THE NOT FIT to automate and grow.\nStart your free 14-day trial today — no credit card required.",
      btn1: "Start Free Trial", btn2: "Talk to Sales",
      check1: "14-day free trial", check2: "No credit card", check3: "Cancel anytime",
    },
    footer: {
      tagline: "AI-powered fitness management platform for gyms, studios, and fitness businesses.",
      legal: "Legal",       legalLinks: ["Privacy Policy", "Terms of Service", "Data Deletion", "Support"],
      copyright: "© 2026 THE NOT FIT. All rights reserved.",
    },
  },

  // ── Albanian / Shqip ───────────────────────────────────────────────────────
  sq: {
    nav: { signIn: "Hyr", getStarted: "Fillo Tani" },
    hero: {
      badge: "Platformë Fitness me AI",
      heading1: "Transformo",
      heading2: "Biznesin Tënd me AI",
      description: "Platforma me inteligjencë artificiale për menaxhimin e palestrës dhe qendrës fitness. Automatizo operacionet, personalizo përvojën e anëtarëve dhe rrit biznesin — gjithçka e drejtuar nga AI.",
      cta1: "Fillo Provën Falas",
      cta2: "Rezervo Demo",
      footnote: "Pa kartë krediti · Provë falas 14-ditore · Anulo kurrë kur",
      membersLabel: "Anëtarë",
    },
    stats: { gyms: "Palestra", activeMembers: "Anëtarë Aktivë", uptimeSla: "Uptime SLA", support: "Mbështetje" },
    features: {
      heading: "Mjete të Drejtuara nga AI",
      subheading: "Module inteligjente që automatizojnë, optimizojnë dhe personalizojnë çdo aspekt të biznesit tuaj fitness",
      cards: [
        { title: "Multi-Tenant White-Label",              desc_short: "Ndërtuar për franshiza dhe zinxhirë",         desc: "Menaxho lokacione të pakufizuara nga një platformë. AI nxjerr trende performancës për çdo vend, ndërsa çdo palestrë ruan markën dhe pavarësinë e saj." },
        { title: "Krijues Promosh me AI",                  desc_short: "Rrit të ardhurat me promo AI",              desc: "AI dizajnon promocione të targetuara, gjen kohën më të mirë të ofertës dhe personalizon zbritjet për të maksimizuar konvertimet — duke kthyer të dhënat në rritje të ardhurave." },
        { title: "Planifikim Klasash me AI",               desc_short: "Menaxhim rezervimesh i optimizuar me AI",  desc: "AI analizon modelet e pranisë dhe kërkesën për të sugjeruar orare optimale, mbushur listat e pritjes dhe balancuar ngarkesën e trajnerëve." },
        { title: "Përpunimi i Pagesave",                   desc_short: "Menaxhim automatik i të ardhurave",        desc: "Faturim i përsëritur, plane pagesash, faturim dhe parashikime financiare të gjeneruara nga AI. Pranoni të gjitha metodat kryesore." },
        { title: "Analitikë e Fuqizuar me AI",             desc_short: "Inteligjencë biznesi parashikuese",       desc: "AI nxjerr trende të ardhurave, parashikon orët kulmore, identifikon anëtarët në rrezik dhe jep rekomandime të zbatueshme në kohë reale." },
        { title: "Plane Ushqyerjeje & Trajnimi me AI",     desc_short: "Programe të gjeneruara nga AI",            desc: "AI ndërton plane personale ushqyerjeje dhe ushtrimesh bazuar në qëllimet, metrikat trupore dhe progresin e çdo anëtari — dhe i përshtat automatikisht." },
      ],
    },
    pricing: {
      heading: "Çmime Modulare Fleksibël",
      subheading: "Paguani vetëm për modulet që keni nevojë. Shtoni ose hiqni veçori ndërsa biznesi juaj rritet.",
      comingSoon: "SHPEJT", soon: "SHPEJT", perMonth: "/muaj", perMonthEach: "/muaj secila",
      modules: {
        business:   { name: "Menaxhimi i Biznesit",  features: ["Hyrje Anëtarësh (QR, Kartë, PIN)", "Panel Multi-Lokacion", "Menaxhimi i Punonjësve", "Raporte & Analitikë"] },
        classes:    { name: "Klasa",                  features: ["Planifikim (i përsëritur & njëherësh)", "Rezervime & lista pritjeje", "Kapacitet & caktim trajnerësh", "Njoftime automatike"] },
        workout:    { name: "Stërvitje & Trajnim",    features: ["Krijues i Planit të Stërvitjes", "Regjistër Stërvitjeje", "Bibliotekë Ushtrimesh", "Raportim Performancës"] },
        diet:       { name: "Planifikues Diete",      features: ["Plane vakte të personalizuara", "Bazë ushqimore (ushqime lokale)", "Gjurmim kalori & makro", "Integrim me planin e stërvitjes"] },
        store:      { name: "Dyqan",                  features: ["Katalog produktesh & kategori", "Menaxhim & gjurmim porosish", "Rrjedha e konfigurueshme", "Ushqime, suplemente, merch & më shumë"] },
        whitelabel: { name: "White Label",            features: ["Aplikacione personale iOS & Android", "Faqe interneti e personalizuar", "Domen i personalizuar", "Personalizim i plotë i markës"] },
        marketing:  { name: "Marketing & CRM",        features: ["CRM me profile anëtarësh", "Rikujtues automatikë rinovimi", "Email, SMS, Push & WhatsApp", "Automatizim onboarding"] },
        payments:   { name: "Pagesa",                 features: ["Integrim Stripe & PayPal", "Pokpay (fintech lokal)", "Faturim i përsëritur", "Raporte & gjurmim pagesash"] },
      },
      extraLocations: { name: "Lokacione Shtesë", features: ["Akses i plotë modulesh për lokacion", "Menaxhim & raporte të veçanta", "Markë e personalizuar për çdo palestrë", "Operacione të pavarura"] },
      calculator: {
        title: "Kalkulator Çmimesh", selectAll: "Zgjidh Të Gjitha", extraLocations: "Lokacione Shtesë",
        modules: (n) => `${n} modul${n !== 1 ? "e" : ""}`,
        extraLocs: (n) => `+${n} lokacion${n !== 1 ? "e" : ""} shtesë`,
        total: "Totali",
      },
      cta: { sub: "Filloni me bazën dhe shtoni module ndërsa rriteni", btn1: "Fillo Tani", btn2: "Fol me Shitjet" },
    },
    showcase: {
      heading: "Shiko AI në Veprim",
      subheading: "Çdo palestrë merr përvojën e saj me markë të plotë. Ja si duken mjetet AI në biznese të ndryshme fitness në platformën tonë.",
      tabs: { diet: "Planifikues Diete AI", workout: "Krijues Stërvitjeje AI", promo: "Krijues Promosh AI" },
      comingSoon: "SHPEJT",
      dietDesc: "AI gjeneron plane vakte të personalizuara me gjurmim makro, baza ushqimore dhe përshtatje automatike — të gjitha me markën e palestrës suaj.",
      workoutDesc: "Programe trajnimi të ndërtuara rreth nivelit të fitnesit, rikuperimit dhe trendeve — planet evoluojnë ndërsa anëtarët përparojnë.",
      promoDesc: "Promocione me AI që targetojnë anëtarët e duhur në kohën e duhur për të maksimizuar të ardhurat dhe mbajtjen.",
    },
    benefits: {
      heading: "Pse THE NOT FIT?",
      subheading: "Platforma më e avancuar e menaxhimit të fitness me AI, e besuar nga liderët e industrisë",
      items: [
        { title: "Menaxhim Diete me AI",              desc: "Gjeneroni plane ushqyerjeje të personalizuara në sekonda. AI adapton makrot dhe oraret e vakteve bazuar në qëllimet, alergjiite dhe metrikat trupore." },
        { title: "Krijues Planesh Stërvitjeje me AI", desc: "Programe trajnimi të ndërtuara rreth nivelit të fitnesit, të dhënave të rikuperimit dhe trendeve të performancës. Planet evoluojnë automatikisht." },
        { title: "Kontroll Aksesi me AI",              desc: "Verifikime identiteti me AI nëpërmjet QR kodit, kartës NFC, PIN ose skanimit biometrik. Zbulimi i anomalive sinjalizon hyrje të dyshimta në kohë reale." },
        { title: "Njohuri Progresi me AI",             desc: "AI analizon përbërjen trupore, rekordet e forcës dhe standardet kardio për të zbuluar trende dhe parashikuar ngecje — duke dhënë hapa të qarta." },
        { title: "Motor Mbajtjeje me AI",              desc: "Modelet e mësimit makinerik parashikojnë largimin para se të ndodhë. AI aktivizon automatikisht oferta riangažhimi, shpërblime dhe fushata rikthimi." },
        { title: "Panel Operacionesh me AI",           desc: "AI parashikon zënien e katit, kërkesat e klasave, optimizon oraret e trajnerëve dhe nxjerr mundësi të ardhurash — live në çdo lokacion." },
      ],
    },
    caseStudy: {
      label: "Rast Studimi", heading1: "Duke Fuqizuar", heading2: "Flex Gym Group",
      description: "Mbi 10,000 anëtarë nëpër lokacione të shumëfishta — të menaxhuar pa probleme në platformën THE NOT FIT. Nga palestra premium deri te studioja CrossFit dhe restorantet e brendshme, një sistem bën gjithçka.",
      gyms: [
        { subtitle: "Palestrë Premium në Kryeqytet",  members: "2,000+", highlight: "Anëtarë", features: ["Kat i plotë pajisjesh", "Klasa grupore", "Trajnim personal", "Sauna & rikuperim"] },
        { subtitle: "Palestrë Premium në Bregdet",    members: "1,500+", highlight: "Anëtarë", features: ["Trajnim në bregdet", "Zona jashtë", "Programe sezonale", "Integrim resort"] },
        { subtitle: "CrossFit, Pilates & Restorant",  members: "1,200+", highlight: "Anëtarë", features: ["Box CrossFit", "Studio Pilates", "Restorant i brendshëm", "Bar ushqyerjeje sportive"] },
      ],
      followInstagram: "Ndiqni në Instagram",
      testimonial: '"THE NOT FIT na lejon të menaxhojmë 3 koncepte krejt të ndryshme palestrash — nga qendra premium fitness deri te box CrossFit me restorant — të gjitha nën një platformë. Anëtarët tanë e duan përvojën e qetë."',
      operator: "Operues fitness me shumë lokacione, Shqipëri",
      statsLabels: ["Lokacione", "Anëtarë Gjithsej", "Klasa / Javë", "Platformë"],
    },
    cta: {
      heading1: "Gati të Shkallëzoni", heading2: "Perandorinë Tuaj me AI?",
      description: "Bashkohuni me bizneset fitness që përdorin mjetet me AI të THE NOT FIT për të automatizuar dhe rritur.\nFilloni provën falas 14-ditore sot — pa kartë krediti.",
      btn1: "Fillo Provën Falas", btn2: "Fol me Shitjet",
      check1: "Provë falas 14-ditore", check2: "Pa kartë krediti", check3: "Anulo kurrë kur",
    },
    footer: {
      tagline: "Platformë e menaxhimit të fitness me AI për palestra, studio dhe biznese fitness.",
      legal: "Ligjore",     legalLinks: ["Politika e Privatësisë", "Kushtet e Shërbimit", "Fshirja e të Dhënave", "Mbështetje"],
      copyright: "© 2026 THE NOT FIT. Të gjitha të drejtat e rezervuara.",
    },
  },

  // ── Italian / Italiano ─────────────────────────────────────────────────────
  it: {
    nav: { signIn: "Accedi", getStarted: "Inizia Ora" },
    hero: {
      badge: "Piattaforma Fitness con AI",
      heading1: "Trasforma il Tuo",
      heading2: "Business Fitness con l'AI",
      description: "La piattaforma basata sull'intelligenza artificiale per la gestione di palestre e centri fitness. Automatizza le operazioni, personalizza l'esperienza dei soci e fai crescere il tuo business — tutto guidato dall'AI.",
      cta1: "Inizia la Prova Gratuita",
      cta2: "Prenota una Demo",
      footnote: "Nessuna carta di credito · Prova gratuita 14 giorni · Cancella quando vuoi",
      membersLabel: "Soci",
    },
    stats: { gyms: "Palestre", activeMembers: "Soci Attivi", uptimeSla: "Uptime SLA", support: "Supporto" },
    features: {
      heading: "Strumenti Guidati dall'AI per Crescere",
      subheading: "Moduli intelligenti che automatizzano, ottimizzano e personalizzano ogni aspetto del tuo business fitness",
      cards: [
        { title: "Multi-Tenancy White-Label",                desc_short: "Costruito per franchising e catene",             desc: "Gestisci sedi illimitate da un'unica piattaforma. L'AI evidenzia trend di performance per sede, mentre ogni palestra mantiene branding e indipendenza." },
        { title: "Creatore Promo con AI",                     desc_short: "Aumenta i ricavi con promo AI",                 desc: "L'AI progetta promozioni mirate, trova il momento migliore per le offerte e personalizza gli sconti per massimizzare le conversioni — trasformando i dati in crescita dei ricavi." },
        { title: "Pianificazione Corsi con AI",              desc_short: "Gestione prenotazioni ottimizzata dall'AI",     desc: "L'AI analizza modelli di presenze e domanda per suggerire orari ottimali, riempire le liste d'attesa e bilanciare i carichi degli istruttori." },
        { title: "Elaborazione Pagamenti",                   desc_short: "Gestione automatica dei ricavi",                desc: "Fatturazione ricorrente, piani di pagamento, fatturazione e previsioni finanziarie generate dall'AI. Accetta tutti i principali metodi di pagamento." },
        { title: "Analisi Potenziate dall'AI",               desc_short: "Business intelligence predittiva",              desc: "L'AI evidenzia trend di ricavi, prevede le ore di punta, identifica i soci a rischio e fornisce raccomandazioni attuabili in tempo reale." },
        { title: "Piani Nutrizionali & Allenamento con AI",  desc_short: "Programmi generati dall'AI",                    desc: "L'AI costruisce piani dieta e allenamento personalizzati basati su obiettivi, metriche corporee e progressi di ogni socio — e li adatta automaticamente." },
      ],
    },
    pricing: {
      heading: "Prezzi Modulari Flessibili",
      subheading: "Paga solo per i moduli di cui hai bisogno. Aggiungi o rimuovi funzionalità man mano che il tuo business cresce.",
      comingSoon: "PROSSIMAMENTE", soon: "PRESTO", perMonth: "/mese", perMonthEach: "/mese cad.",
      modules: {
        business:   { name: "Gestione Aziendale",         features: ["Check-in Soci (QR, Tessera, PIN)", "Dashboard Multi-Sede", "Gestione Dipendenti", "Report & Analisi"] },
        classes:    { name: "Corsi",                       features: ["Pianificazione (ricorrente & una tantum)", "Prenotazioni & liste d'attesa", "Capienza & assegnazione istruttori", "Notifiche automatiche"] },
        workout:    { name: "Allenamento & Training",      features: ["Creatore Piano di Allenamento", "Diario Allenamento", "Libreria Esercizi", "Report Prestazioni"] },
        diet:       { name: "Pianificatore Dieta",         features: ["Piani pasto personalizzati", "Database alimenti (cibi locali)", "Monitoraggio calorie & macro", "Integrazione piano allenamento"] },
        store:      { name: "Negozio",                     features: ["Catalogo prodotti & categorie", "Gestione & monitoraggio ordini", "Flussi ordini configurabili", "Cibo, integratori, merch e altro"] },
        whitelabel: { name: "White Label",                 features: ["App iOS & Android personalizzate", "Sito web personalizzato", "Dominio personalizzato", "Personalizzazione completa del brand"] },
        marketing:  { name: "Marketing & CRM",             features: ["CRM con profili soci", "Promemoria rinnovo automatici", "Email, SMS, Push & WhatsApp", "Automazione onboarding"] },
        payments:   { name: "Pagamenti",                   features: ["Integrazione Stripe & PayPal", "Pokpay (fintech locale)", "Fatturazione ricorrente", "Report & monitoraggio pagamenti"] },
      },
      extraLocations: { name: "Sedi Aggiuntive", features: ["Accesso completo ai moduli per sede", "Gestione & report separati", "Branding personalizzato per palestra", "Operazioni indipendenti"] },
      calculator: {
        title: "Calcolatore Prezzi", selectAll: "Seleziona Tutti", extraLocations: "Sedi Aggiuntive",
        modules: (n) => `${n} modul${n !== 1 ? "i" : "o"}`,
        extraLocs: (n) => `+${n} sed${n !== 1 ? "i" : "e"} aggiuntiv${n !== 1 ? "e" : "a"}`,
        total: "Totale",
      },
      cta: { sub: "Inizia con l'essenziale e aggiungi moduli man mano che cresci", btn1: "Inizia Ora", btn2: "Parla con le Vendite" },
    },
    showcase: {
      heading: "Guarda l'AI in Azione",
      subheading: "Ogni palestra ha la propria esperienza completamente personalizzata. Ecco come appaiono gli strumenti AI nelle diverse attività fitness sulla nostra piattaforma.",
      tabs: { diet: "Pianificatore Dieta AI", workout: "Costruttore Allenamenti AI", promo: "Creatore Promo AI" },
      comingSoon: "PROSSIMAMENTE",
      dietDesc: "L'AI genera piani pasto personalizzati con monitoraggio macro, database alimentari e regolazioni automatiche — tutto con il brand della tua palestra.",
      workoutDesc: "Programmi di allenamento intelligenti basati su livello di fitness, recupero e tendenze — i piani si evolvono con i progressi dei soci.",
      promoDesc: "Promozioni potenziate dall'AI che raggiungono i soci giusti al momento giusto per massimizzare ricavi e fidelizzazione.",
    },
    benefits: {
      heading: "Perché THE NOT FIT?",
      subheading: "La piattaforma di gestione fitness più avanzata, fidata dai leader del settore",
      items: [
        { title: "Gestione Dieta con AI",                   desc: "Genera piani nutrizionali personalizzati in pochi secondi. L'AI adatta i macro e gli orari dei pasti in base agli obiettivi, alle allergie e alle metriche corporee." },
        { title: "Costruttore Piani di Allenamento con AI", desc: "Programmi di allenamento costruiti attorno al livello di fitness, ai dati di recupero e alle tendenze di prestazione. I piani si evolvono automaticamente." },
        { title: "Controllo Accessi con AI",                 desc: "Verifiche d'identità guidate dall'AI tramite QR code, tessera NFC, PIN o scansione biometrica. Il rilevamento anomalie segnala accessi sospetti in tempo reale." },
        { title: "Analisi Progressi con AI",                 desc: "L'AI analizza composizione corporea, record di forza e benchmark cardio per evidenziare trend e prevedere plateau — fornendo passi concreti a istruttori e soci." },
        { title: "Motore di Fidelizzazione con AI",          desc: "Modelli di machine learning prevedono il churn prima che avvenga. L'AI attiva automaticamente offerte personalizzate, premi streak e campagne di riconquista." },
        { title: "Dashboard Operativo con AI",               desc: "L'AI prevede l'occupazione della sala, la domanda dei corsi, ottimizza gli orari degli istruttori e segnala opportunità di ricavo — live in ogni sede." },
      ],
    },
    caseStudy: {
      label: "Caso di Studio", heading1: "Al Servizio del", heading2: "Flex Gym Group",
      description: "Oltre 10.000 soci in più sedi — gestiti senza problemi sulla piattaforma THE NOT FIT. Dalle palestre premium alle box CrossFit ai ristoranti interni, un solo sistema fa tutto.",
      gyms: [
        { subtitle: "Palestra Premium nella Capitale",  members: "2.000+", highlight: "Soci", features: ["Piano attrezzature completo", "Corsi di gruppo", "Personal training", "Sauna & recupero"] },
        { subtitle: "Palestra Premium sul Mare",         members: "1.500+", highlight: "Soci", features: ["Allenamento in riva al mare", "Aree esterne", "Programmi stagionali", "Integrazione resort"] },
        { subtitle: "CrossFit, Pilates & Ristorante",   members: "1.200+", highlight: "Soci", features: ["Box CrossFit", "Studio Pilates", "Ristorante interno", "Bar nutrizione sportiva"] },
      ],
      followInstagram: "Seguici su Instagram",
      testimonial: '"THE NOT FIT ci permette di gestire 3 concetti di palestra completamente diversi — da un centro fitness premium a una box CrossFit con ristorante — tutto sotto un\'unica piattaforma. I nostri soci adorano l\'esperienza."',
      operator: "Operatore fitness multi-sede, Albania",
      statsLabels: ["Sedi", "Soci Totali", "Corsi / Settimana", "Piattaforma"],
    },
    cta: {
      heading1: "Pronto a Scalare il Tuo", heading2: "Impero Fitness con l'AI?",
      description: "Unisciti alle imprese fitness che usano gli strumenti AI di THE NOT FIT per automatizzare e crescere.\nInizia la prova gratuita di 14 giorni oggi — nessuna carta di credito richiesta.",
      btn1: "Inizia la Prova Gratuita", btn2: "Parla con le Vendite",
      check1: "Prova gratuita 14 giorni", check2: "Nessuna carta di credito", check3: "Cancella quando vuoi",
    },
    footer: {
      tagline: "Piattaforma di gestione fitness con AI per palestre, studi e imprese fitness.",
      legal: "Legale",      legalLinks: ["Informativa sulla Privacy", "Termini di Servizio", "Cancellazione Dati", "Supporto"],
      copyright: "© 2026 THE NOT FIT. Tutti i diritti riservati.",
    },
  },
}

// ─── Static data (non-translatable) ──────────────────────────────────────────

const MODULES_CONFIG = [
  { id: "business"   as ModuleKey, price: 149 },
  { id: "classes"    as ModuleKey, price: 149 },
  { id: "workout"    as ModuleKey, price: 99  },
  { id: "diet"       as ModuleKey, price: 99  },
  { id: "store"      as ModuleKey, price: 99  },
  { id: "whitelabel" as ModuleKey, price: 99  },
  { id: "marketing"  as ModuleKey, price: 49, comingSoon: true },
  { id: "payments"   as ModuleKey, price: 49, comingSoon: true },
]

const LOCATION_PRICE = 49

const BENEFIT_ICONS = [
  { color: brandRed,   shape: "square",   iconPath: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
  { color: brandGreen, shape: "circle",   iconPath: "M13 10V3L4 14h7v7l9-11h-7z" },
  { color: brandBlue,  shape: "triangle", iconPath: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
  { color: brandRed,   shape: "square",   iconPath: "M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" },
  { color: brandGreen, shape: "circle",   iconPath: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" },
  { color: brandBlue,  shape: "triangle", iconPath: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
]

const GYM_STATIC = [
  { name: "FlexGym Tirana",    instagram: "https://www.instagram.com/flexgym_tirane/",          image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg",    color: brandRed,   shape: "square"   as const },
  { name: "Flex Green Coast",  instagram: "https://www.instagram.com/flexgymgreencoast.al/",    image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/greencoast.jpg", color: brandGreen, shape: "circle"   as const },
  { name: "Flex Sports Club",  instagram: "https://www.instagram.com/flexsportsclub.al/",       image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/classes.jpg",   color: brandBlue,  shape: "triangle" as const },
]

const TENANT_SHOWCASE_THEMES = [
  { accent: "#E63946", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#FFD60C", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#1B1917", dark: false, bg: "#fafaf9", text: "#1B1917", muted: "#78716c" },
]

// ─── Shapes ───────────────────────────────────────────────────────────────────

const ShapeDarkCtx = createContext(false)
type ShapeProps = { className?: string; size?: number; opacity?: number; animate?: string; color?: string }

const DARK_COLORS: Record<string, string> = {
  [brandRed]: brandRedLight,
  [brandGreen]: brandGreenLight,
  [brandBlue]: brandBlueLight,
}

function useShapeStyle(opacity: number, color: string) {
  const isDark = useContext(ShapeDarkCtx)
  return {
    opacity: isDark ? Math.min(opacity * 3, 0.30) : opacity,
    color: isDark ? (DARK_COLORS[color] ?? color) : color,
  }
}

function ShapeSquare({ className = "", size = 40, opacity = 0.15, animate = "", color = brandRed }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeCircle({ className = "", size = 40, opacity = 0.15, animate = "", color = brandGreen }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rounded-full ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeTriangle({ className = "", size = 40, opacity = 0.15, animate = "", color = brandBlue }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return (
    <svg className={`absolute ${animate} ${className}`} width={size} height={size} viewBox="0 0 40 40" style={{ opacity: s.opacity }}>
      <polygon points="2,2 38,2 20,38" fill={s.color} />
    </svg>
  )
}
function ShapeDiamond({ className = "", size = 40, opacity = 0.15, animate = "", color = brandRed }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rotate-45 ${animate} ${className}`} style={{ width: size, height: size, backgroundColor: s.color, opacity: s.opacity }} />
}
function ShapeCross({ className = "", size = 40, opacity = 0.15, animate = "", color = brandGreen }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  const bar = size * 0.2
  return (
    <div className={`absolute ${animate} ${className}`} style={{ width: size, height: size, opacity: s.opacity }}>
      <div className="absolute top-1/2 left-0 -translate-y-1/2 rounded-full" style={{ width: size, height: bar, backgroundColor: s.color }} />
      <div className="absolute left-1/2 top-0 -translate-x-1/2 rounded-full" style={{ width: bar, height: size, backgroundColor: s.color }} />
    </div>
  )
}
function ShapeRing({ className = "", size = 40, opacity = 0.15, animate = "", color = brandBlue }: ShapeProps) {
  const s = useShapeStyle(opacity, color)
  return <div className={`absolute rounded-full ${animate} ${className}`} style={{ width: size, height: size, border: `${Math.max(2, size * 0.1)}px solid ${s.color}`, opacity: s.opacity }} />
}

function CheckIcon({ color }: { color: string }) {
  return (
    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

// ─── Language Picker ──────────────────────────────────────────────────────────

function LanguagePicker({ lang, setLang, upward = false }: { lang: Lang; setLang: (l: Lang) => void; upward?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = LANGS.find(l => l.code === lang)!

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors select-none"
        aria-label="Select language"
      >
        <span className="text-sm font-medium leading-none">{current.flag}</span>
        <span className="text-sm font-medium leading-none">–</span>
        <span className="text-sm font-medium leading-none">{current.label}</span>
        <svg
          className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className={`absolute right-0 w-44 rounded-2xl border bg-background shadow-xl overflow-hidden z-50 py-1 ${upward ? "bottom-full mb-2" : "top-full mt-2"}`}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false) }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary ${lang === l.code ? "font-semibold" : "font-normal"}`}
            >
              <span className="text-sm leading-none flex-shrink-0">{l.flag}</span>
              <span className="flex-1 text-left leading-none">{l.label}</span>
              {lang === l.code && (
                <svg className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function MainLandingPage() {
  const [selectedModules, setSelectedModules] = useState<Set<ModuleKey>>(new Set(["business"]))
  const [extraLocations, setExtraLocations] = useState(0)
  const [lang, setLang] = useState<Lang>("en")
  const [currency, setCurrency] = useState<"eur" | "usd">("eur")
  const [activeShowcaseTab, setActiveShowcaseTab] = useState(0)
  const [showcasePaused, setShowcasePaused] = useState(false)

  useEffect(() => {
    if (showcasePaused) return
    const timer = setInterval(() => {
      setActiveShowcaseTab(prev => (prev + 1) % 3)
    }, 5000)
    return () => clearInterval(timer)
  }, [showcasePaused])
  const cs = currency === "eur" ? "€" : "$"
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const t = translations[lang]

  const toggleModule = (id: ModuleKey) => {
    setSelectedModules(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedModules(new Set(MODULES_CONFIG.map(m => m.id)))

  const modulesTotal = MODULES_CONFIG.filter(m => selectedModules.has(m.id)).reduce((sum, m) => sum + m.price, 0)
  const locationsTotal = extraLocations * LOCATION_PRICE
  const grandTotal = modulesTotal + locationsTotal

  return (
    <ShapeDarkCtx.Provider value={isDark}>
    <div className="min-h-screen bg-background overflow-hidden">
      <SEOHead
        title="THE NOT FIT - AI-Powered Fitness Management Platform"
        description="AI-powered fitness management platform for modern gym chains and franchises. AI-driven membership management, scheduling, nutrition plans, analytics, and more."
      />

      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5">
              <span className="text-xl font-bold tracking-tight">THE</span>
              <div className="relative w-14 h-14">
                <img src="/logo.png" alt="THE NOT FIT Logo" className="w-full h-full object-contain" loading="eager" />
              </div>
              <span className="text-xl font-bold tracking-tight">FIT</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="ghost" asChild>
                <Link to="/login">{t.nav.signIn}</Link>
              </Button>
              <Button asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white">
                <Link to="/signup">{t.nav.getStarted}</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-20 left-[8%] rotate-12" size={80} opacity={0.08} animate="animate-float" />
          <ShapeCircle className="top-32 right-[12%]" size={120} opacity={0.07} animate="animate-pulse-glow" />
          <ShapeTriangle className="bottom-16 left-[15%] -rotate-12" size={60} opacity={0.08} animate="animate-drift" />
          <ShapeSquare className="bottom-24 right-[8%] rotate-45" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeCircle className="top-48 left-[45%]" size={30} opacity={0.1} animate="animate-fade-float" />
          <ShapeDiamond className="top-12 left-[30%]" size={28} opacity={0.06} animate="animate-spin-slow-reverse" color={brandBlue} />
          <ShapeRing className="bottom-32 right-[25%]" size={70} opacity={0.06} animate="animate-float-delayed" />
          <ShapeCross className="top-[60%] left-[4%]" size={24} opacity={0.07} animate="animate-spin-slow" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2">
                <div className="flex gap-0.5">
                  <div className="w-3 h-3" style={{ backgroundColor: brandRed }} />
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <svg width="12" height="12" viewBox="0 0 12 12"><polygon points="0,0 12,0 6,12" fill={brandBlue} /></svg>
                </div>
                <span className="text-sm font-semibold px-3 py-1 rounded-full bg-secondary text-secondary-foreground">
                  {t.hero.badge}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]">
                {t.hero.heading1}{" "}
                <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                  {t.hero.heading2}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-xl">
                {t.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white text-base px-8">
                  <Link to="/signup">{t.hero.cta1}</Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-base px-8">
                  <Link to="/support">{t.hero.cta2}</Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">{t.hero.footnote}</p>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg"
                  alt="FlexGym Tirana interior"
                  className="w-full h-[480px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 flex items-center justify-center shadow-lg" style={{ backgroundColor: brandRed }}>
                <span className="text-white font-bold text-lg">10+</span>
              </div>
              <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: brandGreen }}>
                <span className="text-white font-bold text-sm text-center leading-tight">10K+<br />{t.hero.membersLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative border-y">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeTriangle className="top-4 left-[6%] rotate-6" size={36} opacity={0.05} animate="animate-float" />
          <ShapeDiamond className="bottom-4 right-[8%]" size={30} opacity={0.05} animate="animate-spin-slow" color={brandGreen} />
          <ShapeRing className="top-1/2 right-[3%] -translate-y-1/2" size={50} opacity={0.04} animate="animate-pulse-glow" color={brandRed} />
          <ShapeCross className="top-6 right-[40%]" size={20} opacity={0.05} animate="animate-drift" color={brandBlue} />
        </div>
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${brandRed}15` }}>
                <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandRed }}>10+</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.gyms}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandGreen}15` }}>
                <svg className="w-6 h-6" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandGreen }}>10,000+</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.activeMembers}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="relative w-12 h-12 flex items-center justify-center">
                <svg width="48" height="48" viewBox="0 0 48 48" style={{ position: "absolute", inset: 0 }}>
                  <polygon points="24,0 48,48 0,48" fill={`${brandBlue}15`} />
                </svg>
                <svg className="w-6 h-6 relative mt-2" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandBlue }}>99.9%</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.uptimeSla}</div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${brandRed}15` }}>
                <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ color: brandRed }}>24/7</div>
                <div className="text-muted-foreground text-sm font-medium mt-1">{t.stats.support}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-20 right-[5%] rotate-6" size={100} opacity={0.05} animate="animate-float" />
          <ShapeCircle className="bottom-32 left-[3%]" size={80} opacity={0.05} animate="animate-drift" />
          <ShapeTriangle className="top-1/2 right-[20%]" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeRing className="top-10 left-[20%]" size={60} opacity={0.04} animate="animate-float-delayed" color={brandRed} />
          <ShapeDiamond className="bottom-16 right-[12%]" size={35} opacity={0.05} animate="animate-spin-slow-reverse" color={brandGreen} />
          <ShapeCross className="bottom-48 left-[8%]" size={28} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
          <ShapeCircle className="top-[35%] left-[50%]" size={22} opacity={0.04} animate="animate-pulse-glow" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center space-y-4 mb-20">
            <div className="flex justify-center gap-0.5 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandRed }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandGreen }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandBlue }} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{t.features.heading}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t.features.subheading}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Cards 0-1: Red / square */}
            {[0, 1].map(i => (
              <Card key={i} className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 flex items-center justify-center mb-4" style={{ backgroundColor: `${brandRed}15` }}>
                    <svg className="w-6 h-6" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 0
                        ? "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        : "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
            {/* Cards 2-3: Green / circle */}
            {[2, 3].map(i => (
              <Card key={i} className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${brandGreen}15` }}>
                    <svg className="w-6 h-6" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 2
                        ? "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        : "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
            {/* Cards 4-5: Blue / triangle */}
            {[4, 5].map(i => (
              <Card key={i} className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg group">
                <CardHeader>
                  <div className="w-12 h-12 flex items-center justify-center mb-4" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                    <svg className="w-6 h-6 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i === 4
                        ? "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        : "M12 6v6m0 0v6m0-6h6m-6 0H6"
                      } />
                    </svg>
                  </div>
                  <CardTitle className="text-xl">{t.features.cards[i].title}</CardTitle>
                  <CardDescription className="text-base">{t.features.cards[i].desc_short}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t.features.cards[i].desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI Product Showcase Section */}
      <section className="relative bg-secondary/20 py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-16 left-[6%] rotate-12" size={70} opacity={0.04} animate="animate-float" />
          <ShapeCircle className="bottom-20 right-[5%]" size={90} opacity={0.04} animate="animate-pulse-glow" />
          <ShapeTriangle className="top-[40%] right-[8%]" size={50} opacity={0.05} animate="animate-drift" />
          <ShapeRing className="bottom-[35%] left-[3%]" size={60} opacity={0.03} animate="animate-float-delayed" color={brandGreen} />
          <ShapeDiamond className="top-12 right-[40%]" size={28} opacity={0.04} animate="animate-spin-slow" color={brandBlue} />
          <ShapeCross className="bottom-12 left-[25%]" size={24} opacity={0.04} animate="animate-fade-float" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center space-y-4 mb-16">
            <div className="flex justify-center gap-0.5 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandRed }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandGreen }} />
              <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandBlue }} />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{t.showcase.heading}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t.showcase.subheading}</p>
          </div>

          <div
            className="max-w-5xl mx-auto"
            onMouseEnter={() => setShowcasePaused(true)}
            onMouseLeave={() => setShowcasePaused(false)}
          >
            {/* Tab buttons */}
            <div className="flex justify-center gap-2 mb-8">
              {[
                { label: t.showcase.tabs.diet, color: brandRed, idx: 0 },
                { label: t.showcase.tabs.workout, color: brandGreen, idx: 1 },
                { label: t.showcase.tabs.promo, color: brandBlue, idx: 2, comingSoon: true },
              ].map(tab => (
                <button
                  key={tab.idx}
                  onClick={() => setActiveShowcaseTab(tab.idx)}
                  className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeShowcaseTab === tab.idx
                      ? "text-white shadow-lg"
                      : "text-muted-foreground hover:text-foreground bg-secondary/80 hover:bg-secondary"
                  }`}
                  style={activeShowcaseTab === tab.idx ? { backgroundColor: tab.color } : undefined}
                >
                  <span className="flex items-center gap-2">
                    {tab.label}
                    {tab.comingSoon && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/20">{t.showcase.comingSoon}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Description under tabs */}
            <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto transition-opacity duration-300">
              {activeShowcaseTab === 0 ? t.showcase.dietDesc : activeShowcaseTab === 1 ? t.showcase.workoutDesc : t.showcase.promoDesc}
            </p>

            {/* Mock screen container */}
            <div className="relative rounded-2xl border shadow-2xl overflow-hidden" style={{ backgroundColor: TENANT_SHOWCASE_THEMES[activeShowcaseTab].bg, transition: "background-color 0.5s" }}>
              {/* Tab 0: AI Diet Planner (FlexGym) */}
              <div className={`transition-all duration-500 ${activeShowcaseTab === 0 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                {(() => {
                  const th = TENANT_SHOWCASE_THEMES[0]
                  return (
                    <div className="p-6 md:p-8" style={{ color: th.text }}>
                      {/* Top bar */}
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
                          <div className="text-xl font-bold">Weight Loss Plan</div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${th.accent}20` }}>
                          <svg className="w-4 h-4" style={{ color: th.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="text-xs font-semibold" style={{ color: th.accent }}>AI Generated</span>
                        </div>
                      </div>

                      {/* Day tabs */}
                      <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
                        {["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"].map((day, i) => (
                          <div
                            key={day}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors"
                            style={i === 0
                              ? { backgroundColor: th.accent, color: th.dark ? "#fff" : "#fff" }
                              : { backgroundColor: th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", color: th.muted }
                            }
                          >
                            {day}
                          </div>
                        ))}
                      </div>

                      {/* Meal cards grid */}
                      <div className="grid md:grid-cols-3 gap-4 mb-6">
                        {[
                          { meal: "Breakfast", time: "7:30 AM", foods: [{ name: "Greek Yogurt with Berries", cal: 180, g: "150g" }, { name: "Whole Grain Toast", cal: 120, g: "60g" }, { name: "Green Tea", cal: 5, g: "250ml" }], kcal: 305 },
                          { meal: "Lunch", time: "12:30 PM", foods: [{ name: "Grilled Chicken Salad", cal: 350, g: "200g" }, { name: "Quinoa", cal: 185, g: "120g" }, { name: "Olive Oil Dressing", cal: 90, g: "15ml" }], kcal: 625 },
                          { meal: "Dinner", time: "7:00 PM", foods: [{ name: "Baked Salmon Fillet", cal: 380, g: "180g" }, { name: "Steamed Broccoli", cal: 55, g: "150g" }, { name: "Brown Rice", cal: 215, g: "140g" }], kcal: 650 },
                        ].map(card => (
                          <div key={card.meal} className="rounded-xl p-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-semibold">{card.meal}</span>
                              <span className="text-xs" style={{ color: th.muted }}>{card.time}</span>
                            </div>
                            <div className="space-y-2 mb-3">
                              {card.foods.map(food => (
                                <div key={food.name} className="flex items-center justify-between text-xs">
                                  <span className="truncate mr-2">{food.name}</span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span style={{ color: th.muted }}>{food.g}</span>
                                    <span className="font-medium">{food.cal}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t pt-2 flex justify-between items-center text-xs" style={{ borderColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
                              <span style={{ color: th.muted }}>Total</span>
                              <span className="font-bold" style={{ color: th.accent }}>{card.kcal} kcal</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Macro bars + total */}
                      <div className="rounded-xl p-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold">Daily Summary</span>
                          <span className="text-lg font-bold" style={{ color: th.accent }}>1,580 kcal</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { label: "Protein", value: "126g", pct: 72 },
                            { label: "Carbs", value: "148g", pct: 58 },
                            { label: "Fat", value: "52g", pct: 45 },
                          ].map(macro => (
                            <div key={macro.label}>
                              <div className="flex justify-between text-xs mb-1">
                                <span style={{ color: th.muted }}>{macro.label}</span>
                                <span className="font-medium">{macro.value}</span>
                              </div>
                              <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
                                <div className="h-full rounded-full transition-all" style={{ width: `${macro.pct}%`, backgroundColor: th.accent }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Tab 1: AI Workout Builder (Repeat) */}
              <div className={`transition-all duration-500 ${activeShowcaseTab === 1 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                {(() => {
                  const th = TENANT_SHOWCASE_THEMES[1]
                  return (
                    <div className="p-6 md:p-8" style={{ color: th.text }}>
                      {/* Top bar */}
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <div className="text-xs font-medium mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
                          <div className="text-xl font-bold">Full Body Strength</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${th.accent}25`, color: th.accent }}>Intermediate</span>
                            <span className="text-xs" style={{ color: th.muted }}>45 min</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${th.accent}20` }}>
                          <svg className="w-4 h-4" style={{ color: th.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          <span className="text-xs font-semibold" style={{ color: th.accent }}>AI Generated</span>
                        </div>
                      </div>

                      {/* Muscle group tags */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {["Chest", "Back", "Legs", "Core", "Shoulders"].map(muscle => (
                          <span key={muscle} className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: `${th.accent}15`, color: th.accent }}>
                            {muscle}
                          </span>
                        ))}
                      </div>

                      {/* Exercise table */}
                      <div className="rounded-xl overflow-hidden mb-6" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-xs font-semibold" style={{ color: th.muted, borderBottom: `1px solid ${th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                          <div className="col-span-5">Exercise</div>
                          <div className="col-span-2 text-center">Sets</div>
                          <div className="col-span-2 text-center">Reps</div>
                          <div className="col-span-3 text-center">Rest</div>
                        </div>
                        {[
                          { name: "Barbell Bench Press", sets: 4, reps: "8-10", rest: "90s" },
                          { name: "Bent-Over Row", sets: 4, reps: "8-10", rest: "90s" },
                          { name: "Barbell Squat", sets: 4, reps: "6-8", rest: "120s" },
                          { name: "Overhead Press", sets: 3, reps: "10-12", rest: "60s" },
                          { name: "Plank Hold", sets: 3, reps: "45s", rest: "45s" },
                        ].map((ex, i) => (
                          <div key={ex.name} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm items-center" style={{ borderBottom: i < 4 ? `1px solid ${th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` : undefined }}>
                            <div className="col-span-5 font-medium">{ex.name}</div>
                            <div className="col-span-2 text-center" style={{ color: th.accent }}>{ex.sets}</div>
                            <div className="col-span-2 text-center">{ex.reps}</div>
                            <div className="col-span-3 text-center" style={{ color: th.muted }}>{ex.rest}</div>
                          </div>
                        ))}
                      </div>

                      {/* Progress / footer */}
                      <div className="rounded-xl p-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold">Workout Progress</span>
                          <span className="text-xs font-medium" style={{ color: th.accent }}>3 of 5 completed</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
                          <div className="h-full rounded-full transition-all" style={{ width: "60%", backgroundColor: th.accent }} />
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Tab 2: AI Promo Builder (Hark) — Coming Soon */}
              <div className={`transition-all duration-500 ${activeShowcaseTab === 2 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                {(() => {
                  const th = TENANT_SHOWCASE_THEMES[2]
                  return (
                    <div className="relative p-6 md:p-8" style={{ color: th.text }}>
                      {/* Blurred content behind overlay */}
                      <div className="filter blur-[2px] opacity-60 pointer-events-none select-none">
                        {/* Top bar */}
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <div className="text-xs font-medium mb-1" style={{ color: th.muted }}>AI-GENERATED PROMOTION</div>
                            <div className="text-xl font-bold">Summer Membership Drive</div>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${th.accent}15` }}>
                            <svg className="w-4 h-4" style={{ color: th.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            <span className="text-xs font-semibold" style={{ color: th.accent }}>AI Generated</span>
                          </div>
                        </div>

                        {/* Promo cards grid */}
                        <div className="grid md:grid-cols-3 gap-4 mb-6">
                          {[
                            { title: "New Member Welcome", discount: "30%", audience: "New Sign-ups", period: "First Month" },
                            { title: "Annual Plan Upgrade", discount: "20%", audience: "Monthly Members", period: "Limited Time" },
                            { title: "Referral Bonus", discount: "€25", audience: "Active Members", period: "Ongoing" },
                          ].map(promo => (
                            <div key={promo.title} className="rounded-xl p-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                              <div className="text-2xl font-bold mb-1" style={{ color: th.accent }}>{promo.discount}</div>
                              <div className="text-sm font-semibold mb-2">{promo.title}</div>
                              <div className="space-y-1 text-xs" style={{ color: th.muted }}>
                                <div className="flex justify-between"><span>Target</span><span>{promo.audience}</span></div>
                                <div className="flex justify-between"><span>Period</span><span>{promo.period}</span></div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* AI insights bar */}
                        <div className="rounded-xl p-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
                          <div className="grid grid-cols-3 gap-4">
                            {[
                              { label: "Predicted Conversion", value: "24%" },
                              { label: "Estimated Revenue", value: "€3,200" },
                              { label: "Members Targeted", value: "450" },
                            ].map(stat => (
                              <div key={stat.label} className="text-center">
                                <div className="text-lg font-bold" style={{ color: th.accent }}>{stat.value}</div>
                                <div className="text-xs" style={{ color: th.muted }}>{stat.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Coming Soon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-white font-bold text-lg shadow-xl" style={{ backgroundColor: brandBlue }}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            {t.showcase.comingSoon}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mt-6">
              {[0, 1, 2].map(i => (
                <button
                  key={i}
                  onClick={() => setActiveShowcaseTab(i)}
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: activeShowcaseTab === i ? 32 : 8,
                    backgroundColor: activeShowcaseTab === i
                      ? [brandRed, brandGreen, brandBlue][i]
                      : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeTriangle className="top-12 left-[5%] rotate-12" size={70} opacity={0.04} animate="animate-float" />
          <ShapeSquare className="bottom-20 right-[8%]" size={90} opacity={0.04} animate="animate-spin-slow" />
          <ShapeCircle className="top-[30%] right-[4%]" size={55} opacity={0.04} animate="animate-drift" />
          <ShapeRing className="bottom-[40%] left-[3%]" size={80} opacity={0.03} animate="animate-pulse-glow" color={brandRed} />
          <ShapeDiamond className="top-24 right-[45%]" size={26} opacity={0.05} animate="animate-fade-float" color={brandBlue} />
          <ShapeCross className="bottom-12 left-[20%]" size={22} opacity={0.04} animate="animate-spin-slow-reverse" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{t.pricing.heading}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t.pricing.subheading}</p>
            <div className="flex justify-center pt-2">
              <div className="inline-flex rounded-full border p-1 bg-background">
                <button
                  onClick={() => setCurrency("eur")}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                    currency === "eur"
                      ? "text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={currency === "eur" ? { backgroundColor: brandRed } : undefined}
                >
                  EUR (€)
                </button>
                <button
                  onClick={() => setCurrency("usd")}
                  className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                    currency === "usd"
                      ? "text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={currency === "usd" ? { backgroundColor: brandRed } : undefined}
                >
                  USD ($)
                </button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto mb-12">
            {/* Business Management */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.business.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>{cs}149</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.business.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Classes */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.classes.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>{cs}149</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.classes.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Workout & Training */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.workout.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>{cs}99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.workout.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Diet Planner */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.diet.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>{cs}99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.diet.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Store */}
            <Card className="border-2 hover:border-[#4A8C3F]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brandGreen}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandGreen }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.store.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandGreen }}>{cs}99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.store.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandGreen} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* White Label */}
            <Card className="border-2 hover:border-[#C0392B]/50 transition-all hover:shadow-lg">
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.whitelabel.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>{cs}99</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.whitelabel.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Marketing & CRM — Coming Soon */}
            <Card className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg relative">
              <div className="absolute -top-3 right-4">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: brandBlue }}>{t.pricing.comingSoon}</span>
              </div>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                  <svg className="w-5 h-5 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.marketing.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandBlue }}>{cs}49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.marketing.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandBlue} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Payments — Coming Soon */}
            <Card className="border-2 hover:border-[#2E5BA8]/50 transition-all hover:shadow-lg relative">
              <div className="absolute -top-3 right-4">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: brandBlue }}>{t.pricing.comingSoon}</span>
              </div>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandBlue}15`, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                  <svg className="w-5 h-5 mb-1" style={{ color: brandBlue }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.modules.payments.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandBlue }}>{cs}49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonth}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.modules.payments.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandBlue} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Extra Locations */}
            <Card className="border-2 transition-all hover:shadow-lg" style={{ borderColor: `${brandRed}40` }}>
              <CardHeader className="pb-3">
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${brandRed}15` }}>
                  <svg className="w-5 h-5" style={{ color: brandRed }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{t.pricing.extraLocations.name}</CardTitle>
                <div className="flex items-baseline gap-1 pt-1">
                  <span className="text-3xl font-bold" style={{ color: brandRed }}>{cs}49</span>
                  <span className="text-muted-foreground text-sm">{t.pricing.perMonthEach}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {t.pricing.extraLocations.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckIcon color={brandRed} /><span>{f}</span></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Calculator */}
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 bg-background/80 backdrop-blur">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold">{t.pricing.calculator.title}</h3>
                  <button
                    onClick={selectAll}
                    className="text-xs font-medium px-3 py-1.5 rounded-full border hover:bg-secondary transition-colors"
                  >
                    {t.pricing.calculator.selectAll}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                  {MODULES_CONFIG.map((m) => (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedModules.has(m.id)
                          ? "border-[#C0392B]/50 bg-[#C0392B]/5"
                          : "border-border hover:border-border/80"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.has(m.id)}
                        onChange={() => toggleModule(m.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                          selectedModules.has(m.id) ? "border-[#C0392B] bg-[#C0392B]" : "border-muted-foreground/30"
                        }`}
                      >
                        {selectedModules.has(m.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{t.pricing.modules[m.id].name}</span>
                          {"comingSoon" in m && m.comingSoon && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: brandBlue }}>{t.pricing.soon}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold flex-shrink-0">{cs}{m.price}</span>
                    </label>
                  ))}
                </div>

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{t.pricing.calculator.extraLocations}</span>
                    <span className="text-sm text-muted-foreground">{cs}{LOCATION_PRICE}{t.pricing.perMonthEach}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setExtraLocations(Math.max(0, extraLocations - 1))}
                      className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors text-lg font-medium"
                    >
                      &minus;
                    </button>
                    <div className="flex-1 bg-secondary/50 rounded-lg h-9 flex items-center justify-center">
                      <span className="text-sm font-bold">{extraLocations}</span>
                    </div>
                    <button
                      onClick={() => setExtraLocations(extraLocations + 1)}
                      className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors text-lg font-medium"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.pricing.calculator.modules(selectedModules.size)}</span>
                    <span className="font-medium">{cs}{modulesTotal}{t.pricing.perMonth}</span>
                  </div>
                  {extraLocations > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.pricing.calculator.extraLocs(extraLocations)}</span>
                      <span className="font-medium">{cs}{locationsTotal}{t.pricing.perMonth}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2 border-t">
                    <span className="font-bold text-lg">{t.pricing.calculator.total}</span>
                    <span className="text-2xl font-bold" style={{ color: brandRed }}>{cs}{grandTotal}<span className="text-sm font-medium text-muted-foreground">{t.pricing.perMonth}</span></span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-muted-foreground mb-6">{t.pricing.cta.sub}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white">
                <Link to="/signup">{t.pricing.cta.btn1}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/support">{t.pricing.cta.btn2}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative bg-secondary/20 py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-16 right-[6%] rotate-12" size={65} opacity={0.05} animate="animate-float-delayed" />
          <ShapeCircle className="bottom-20 left-[5%]" size={90} opacity={0.04} animate="animate-pulse-glow" />
          <ShapeTriangle className="top-[40%] left-[2%]" size={40} opacity={0.05} animate="animate-drift" />
          <ShapeRing className="top-12 left-[40%]" size={45} opacity={0.04} animate="animate-spin-slow" color={brandGreen} />
          <ShapeDiamond className="bottom-32 right-[15%]" size={32} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
        </div>
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            <div className="relative flex justify-center lg:justify-center">
              <div className="absolute inset-0 blur-3xl opacity-20 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(ellipse at center, ${brandRed} 0%, ${brandBlue} 60%, transparent 100%)` }}
              />
              <div className="relative z-10 flex-shrink-0" style={{ width: 280, filter: "drop-shadow(0 32px 64px rgba(0,0,0,0.45))" }}>
                <div className="relative rounded-[44px] p-[3px]" style={{ background: "linear-gradient(145deg, #3a3a3c 0%, #1c1c1e 50%, #3a3a3c 100%)" }}>
                  <div className="absolute -left-[3px] top-[88px] w-[3px] h-8 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -left-[3px] top-[132px] w-[3px] h-12 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -left-[3px] top-[156px] w-[3px] h-12 rounded-l-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="absolute -right-[3px] top-[120px] w-[3px] h-16 rounded-r-full" style={{ backgroundColor: "#3a3a3c" }} />
                  <div className="rounded-[42px] overflow-hidden bg-black">
                    <div className="relative bg-black flex justify-center pt-3 pb-1 px-6">
                      <div className="w-24 h-6 rounded-full bg-black border border-zinc-800 flex items-center justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-zinc-700" />
                        <div className="w-3 h-3 rounded-full bg-zinc-700" />
                      </div>
                    </div>
                    <div className="relative" style={{ aspectRatio: "9/18" }}>
                      <video src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexaccess.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                    </div>
                    <div className="bg-black flex justify-center py-2">
                      <div className="w-24 h-1 rounded-full bg-zinc-600" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 items-end justify-center mt-5">
                  <div className="w-8 h-8" style={{ backgroundColor: brandRed, opacity: 0.9 }} />
                  <div className="w-6 h-6 rounded-full" style={{ backgroundColor: brandGreen, opacity: 0.9 }} />
                  <svg width="24" height="24" viewBox="0 0 40 40" style={{ opacity: 0.9 }}>
                    <polygon points="2,2 38,2 20,38" fill={brandBlue} />
                  </svg>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex gap-0.5 mb-4">
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandRed }} />
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <div className="w-8 h-1 rounded-full" style={{ backgroundColor: brandBlue }} />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                  {(() => {
                    const parts = t.benefits.heading.split("NOT")
                    if (parts.length === 2) {
                      return (
                        <>
                          {parts[0]}
                          <span className="inline-flex items-center gap-2 align-baseline mx-1">
                            <span className="inline-block w-[0.7em] h-[0.7em]" style={{ backgroundColor: brandRed }} />
                            <span className="inline-block w-[0.7em] h-[0.7em] rounded-full" style={{ backgroundColor: brandGreen }} />
                            <svg className="inline-block w-[0.7em] h-[0.7em]" viewBox="0 0 40 40"><polygon points="2,2 38,2 20,38" fill={brandBlue} /></svg>
                          </span>
                          {parts[1]}
                        </>
                      )
                    }
                    return t.benefits.heading
                  })()}
                </h2>
                <p className="text-xl text-muted-foreground">{t.benefits.subheading}</p>
              </div>

              <div className="space-y-5">
                {BENEFIT_ICONS.map((icon, i) => {
                  const item = t.benefits.items[i]
                  return (
                    <div key={i} className="flex items-start gap-4">
                      {icon.shape === "triangle" ? (
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: icon.color, clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" }}>
                          <svg className="w-5 h-5 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.iconPath} />
                          </svg>
                        </div>
                      ) : (
                        <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${icon.shape === "circle" ? "rounded-full" : ""}`} style={{ backgroundColor: icon.color }}>
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon.iconPath} />
                          </svg>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold mb-0.5">{item.title}</h3>
                        <p className="text-muted-foreground text-sm">{item.desc}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study — Flex Gym Group */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeCircle className="top-12 left-[10%]" size={60} opacity={0.06} animate="animate-float" />
          <ShapeSquare className="bottom-12 right-[10%] rotate-12" size={50} opacity={0.06} animate="animate-spin-slow" />
          <ShapeTriangle className="top-24 right-[20%]" size={40} opacity={0.05} animate="animate-drift" />
          <ShapeRing className="bottom-[35%] left-[4%]" size={55} opacity={0.04} animate="animate-pulse-glow" color={brandRed} />
          <ShapeCross className="top-[50%] right-[5%]" size={26} opacity={0.04} animate="animate-float-delayed" color={brandGreen} />
          <ShapeDiamond className="bottom-20 left-[35%]" size={24} opacity={0.04} animate="animate-fade-float" color={brandBlue} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">
                  <div className="w-2.5 h-2.5" style={{ backgroundColor: brandRed }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: brandGreen }} />
                  <svg width="10" height="10" viewBox="0 0 10 10"><polygon points="0,0 10,0 5,10" fill={brandBlue} /></svg>
                </div>
                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t.caseStudy.label}</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                {t.caseStudy.heading1}{" "}
                <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                  {t.caseStudy.heading2}
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t.caseStudy.description}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {GYM_STATIC.map((gym, i) => {
                const info = t.caseStudy.gyms[i]
                return (
                  <div key={i} className="group relative bg-card border rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                    <div className="relative h-52 overflow-hidden">
                      <img src={gym.image} alt={gym.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                        <div>
                          <h3 className="text-white font-bold text-lg">{gym.name}</h3>
                          <p className="text-white/80 text-sm">{info.subtitle}</p>
                        </div>
                        <div
                          className={`w-12 h-12 flex items-center justify-center text-white font-bold text-xs shadow-lg ${gym.shape === "circle" ? "rounded-full" : ""}`}
                          style={{ backgroundColor: gym.color, ...(gym.shape === "triangle" ? { clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" } : {}) }}
                        >
                          {gym.shape !== "triangle" && info.members}
                        </div>
                      </div>
                    </div>
                    <div className="p-5 space-y-4">
                      <div className="flex items-center gap-2 -mt-1 mb-1">
                        <span className="text-xl font-bold" style={{ color: gym.color }}>{info.members}</span>
                        <span className="text-sm text-muted-foreground">{info.highlight}</span>
                      </div>
                      <ul className="space-y-2">
                        {info.features.map((f, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <div
                              className={`w-1.5 h-1.5 flex-shrink-0 ${gym.shape === "circle" ? "rounded-full" : ""}`}
                              style={{ backgroundColor: gym.color, ...(gym.shape === "triangle" ? { clipPath: "polygon(0% 0%, 100% 0%, 50% 100%)" } : {}) }}
                            />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <a
                        href={gym.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium hover:underline transition-colors"
                        style={{ color: gym.color }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                        {t.caseStudy.followInstagram}
                      </a>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="bg-card border rounded-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5" style={{ color: brandRed }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="text-lg md:text-xl font-medium italic leading-relaxed">
                    {t.caseStudy.testimonial}
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 rounded-lg overflow-hidden" style={{ backgroundColor: "#000000", padding: "8px" }}>
                      <img src="https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" alt="Flex Gym Group logo" className="h-10 w-auto object-contain" />
                    </div>
                    <div className="space-y-0.5">
                      <div className="font-semibold">Flex Gym Group</div>
                      <div className="text-muted-foreground text-sm">{t.caseStudy.operator}</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: "3",      color: brandRed },
                    { value: "10,000+", color: brandGreen },
                    { value: "50+",    color: brandBlue },
                    { value: "1",      color: brandRed },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 rounded-xl" style={{ backgroundColor: `${stat.color}10` }}>
                      <div className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t.caseStudy.statsLabels[i]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-1/4 left-[5%] rotate-45" size={150} opacity={0.04} animate="animate-spin-slow" />
          <ShapeCircle className="bottom-1/4 right-[5%]" size={200} opacity={0.04} animate="animate-pulse-glow" />
          <ShapeTriangle className="top-1/3 right-[15%] rotate-12" size={100} opacity={0.04} animate="animate-float" />
          <ShapeRing className="top-[15%] left-[25%]" size={70} opacity={0.04} animate="animate-drift" color={brandGreen} />
          <ShapeDiamond className="bottom-[20%] left-[12%]" size={40} opacity={0.04} animate="animate-float-delayed" color={brandBlue} />
          <ShapeCross className="top-[60%] right-[25%]" size={30} opacity={0.03} animate="animate-spin-slow-reverse" color={brandRed} />
          <ShapeCircle className="top-8 left-[50%]" size={35} opacity={0.04} animate="animate-fade-float" color={brandRed} />
        </div>

        <div className="container mx-auto px-4 text-center relative">
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="flex justify-center gap-1 mb-6">
              <div className="w-10 h-10" style={{ backgroundColor: brandRed }} />
              <div className="w-10 h-10 rounded-full" style={{ backgroundColor: brandGreen }} />
              <svg width="40" height="40" viewBox="0 0 40 40"><polygon points="2,2 38,2 20,38" fill={brandBlue} /></svg>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
              {t.cta.heading1}{" "}
              <span className="bg-gradient-to-r from-[#C0392B] via-[#4A8C3F] to-[#2E5BA8] bg-clip-text text-transparent">
                {t.cta.heading2}
              </span>
            </h2>
            <p className="text-xl text-muted-foreground">
              {t.cta.description.split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button size="lg" asChild style={{ backgroundColor: brandRed }} className="hover:opacity-90 text-white text-lg px-8 py-6 h-auto">
                <Link to="/signup">{t.cta.btn1}</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8 py-6 h-auto">
                <Link to="/support">{t.cta.btn2}</Link>
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><CheckIcon color={brandRed} /><span>{t.cta.check1}</span></div>
              <div className="flex items-center gap-2"><CheckIcon color={brandGreen} /><span>{t.cta.check2}</span></div>
              <div className="flex items-center gap-2"><CheckIcon color={brandBlue} /><span>{t.cta.check3}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t bg-background overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <ShapeSquare className="top-8 right-[6%] rotate-12" size={30} opacity={0.03} animate="animate-float" />
          <ShapeCircle className="bottom-6 left-[10%]" size={40} opacity={0.03} animate="animate-drift" />
          <ShapeTriangle className="top-1/2 right-[30%] -translate-y-1/2" size={25} opacity={0.03} animate="animate-spin-slow" />
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
            <div className="space-y-4">
              <Link to="/" className="flex items-center gap-1.5">
                <span className="font-bold">THE</span>
                <div className="relative w-11 h-11">
                  <img src="/logo.png" alt="THE NOT FIT Logo" className="w-full h-full object-contain" />
                </div>
                <span className="font-bold">FIT</span>
              </Link>
              <p className="text-sm text-muted-foreground max-w-xs">{t.footer.tagline}</p>
              <div className="flex gap-0.5 pt-2">
                <div className="w-4 h-4" style={{ backgroundColor: brandRed }} />
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: brandGreen }} />
                <svg width="16" height="16" viewBox="0 0 16 16"><polygon points="0,0 16,0 8,16" fill={brandBlue} /></svg>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.legal}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {["/privacy", "/privacy", "/data-deletion", "/support"].map((href, i) => (
                  <li key={i}><Link to={href} className="hover:text-foreground transition-colors">{t.footer.legalLinks[i]}</Link></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              <LanguagePicker lang={lang} setLang={setLang} upward />
              <p>{t.footer.copyright}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </ShapeDarkCtx.Provider>
  )
}
